import { useState } from "react";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import { PrimaryButton, SecondaryButton } from "../ui";
import { RTL } from "../../cache/CacheRtl";
import { t } from "../dashboard/coach/coachTokens";

/**
 * Add a food to the catalog.
 *
 * <p>The scope is not asked for and cannot be chosen: the endpoint the caller reaches decides it. A
 * company admin posting here creates a company food; a system admin creates a system food. That is the
 * same rule the server enforces from the security context, so offering a picker would only invite a
 * choice the server is going to overrule.
 *
 * <p>This replaces the legacy "new food" dialog, which carried a "מקור המזון" (origin) select —
 * ADMIN / USER / EXTERNAL / AI. That axis described what kind of actor made a row and nothing filtered
 * or authorised on it; scope answers the question it was gesturing at, and answers it truthfully.
 *
 * <p>One serving is required because a food with no portion cannot be logged or put in a menu. More
 * portions are added from the food's own page once it exists.
 */
const FoodDictionaryCreateDialog = ({ open, onClose, onCreate }) => {
  const empty = {
    name: "",
    brand: "",
    barcode: "",
    servingName: "100 גרם",
    gramsPerServing: "100",
    calories: "",
    protein: "",
    carbs: "",
    fats: "",
  };
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const set = (key) => (e) => setForm((p) => ({ ...p, [key]: e.target.value }));

  // Name and calories are what the server insists on; everything else is genuinely optional, and a
  // serving with no macros is still a usable portion.
  const canSave = form.name.trim() !== "" && form.calories !== "" && !saving;

  const num = (v) => (v === "" || v === null ? null : Number(v));

  const close = () => {
    setForm(empty);
    setError(null);
    onClose();
  };

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      await onCreate({
        name: form.name.trim(),
        brand: form.brand.trim() || null,
        barcode: form.barcode.trim() || null,
        servingName: form.servingName.trim() || "מנה",
        gramsPerServing: num(form.gramsPerServing),
        calories: num(form.calories),
        protein: num(form.protein),
        carbs: num(form.carbs),
        fats: num(form.fats),
      });
      close();
    } catch (e) {
      setError(e?.response?.data?.errorMessage || "שמירת המזון נכשלה");
    } finally {
      setSaving(false);
    }
  };

  // Every field goes through RTL. `dir="rtl"` on the Dialog flips the layout but not the emotion
  // cache MUI uses to place a TextField's label and its outline notch, so without this the labels
  // sit on the left of right-aligned inputs — which is the "strangely to the left" look. The rest of
  // the app already wraps its fields this way.
  const field = (label, key, extra = {}) => (
    <RTL>
      <TextField
        label={label}
        value={form[key]}
        onChange={set(key)}
        size="small"
        fullWidth
        {...extra}
      />
    </RTL>
  );

  return (
    <Dialog open={open} onClose={close} maxWidth="sm" fullWidth dir="rtl">
      <DialogTitle sx={{ fontSize: "18px", fontWeight: 800, color: t.text }}>מזון חדש</DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: "14px", pt: "6px" }}>
          {field("שם המזון", "name", { required: true, autoFocus: true })}

          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: "12px" }}>
            {field("מותג", "brand")}
            {field("ברקוד", "barcode")}
          </Box>

          <Typography sx={{ fontSize: "13px", fontWeight: 700, color: t.t2, mt: "4px" }}>
            מנה ראשונה
          </Typography>

          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: "12px" }}>
            {field("שם המנה", "servingName")}
            {field("גרם למנה", "gramsPerServing", { type: "number" })}
          </Box>

          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(4, 1fr)" }, gap: "12px" }}>
            {field("קלוריות", "calories", { type: "number", required: true })}
            {field("חלבון", "protein", { type: "number" })}
            {field("פחמימות", "carbs", { type: "number" })}
            {field("שומן", "fats", { type: "number" })}
          </Box>

          {error && (
            <Typography sx={{ fontSize: "13px", color: t.redText }}>{error}</Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <SecondaryButton onClick={close}>בטל</SecondaryButton>
        <PrimaryButton onClick={submit} disabled={!canSave}>
          {saving ? "שומר..." : "צור מזון"}
        </PrimaryButton>
      </DialogActions>
    </Dialog>
  );
};

export default FoodDictionaryCreateDialog;
